/**
 * Per-user, per-device sidebar-navigation frequency counter.
 *
 * Stored in ``localStorage`` under ``diplomexa_nav_freq_v1`` as
 * ``{ [path]: count }``.  ``recordVisit(path)`` is called from
 * every sidebar ``<NavLink>`` and from the ⌘K palette "nav match"
 * click.  ``topPaths(navItems, n)`` returns the ``n`` most-visited
 * items the user actually has access to.
 *
 * Design notes:
 *   - Cap at 200 entries with LRU-ish decay so old roles don't
 *     dominate forever after a role switch.
 *   - Never throws — localStorage may be disabled in iframes /
 *     Incognito; treat as "no history yet".
 *   - Counters are bounded so a single page can't dominate the
 *     ordering after thousands of visits (returns ``Math.log`` of
 *     the raw count for sort purposes).
 */
const STORAGE_KEY = "diplomexa_nav_freq_v1";
const MAX_ENTRIES = 200;

function _load() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function _save(map) {
  if (typeof window === "undefined") return;
  try {
    // Evict the lowest-frequency entries if the map has gotten too
    // big after a role change or extensive use.
    const entries = Object.entries(map);
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b[1] - a[1]);
      map = Object.fromEntries(entries.slice(0, MAX_ENTRIES));
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* localStorage full or disabled — silently drop. */
  }
}

export function recordVisit(path) {
  if (!path) return;
  const map = _load();
  map[path] = (map[path] || 0) + 1;
  _save(map);
}

/**
 * Return the top ``n`` most-visited nav items in ``navItems`` that
 * appear in the recorded history.  Falls back gracefully:
 *   - If history < 2 entries → return the first ``n`` items in the
 *     given order (so brand-new users still see something useful).
 *   - Items the user has visited 0 times are not surfaced.
 */
export function topPaths(navItems, n = 5) {
  const map = _load();
  if (Object.keys(map).length < 2) {
    return { items: navItems.slice(0, n), kind: "default" };
  }
  // Score each item: log(count + 1) so a 100-click power-user doesn't
  // monopolise the list.
  const scored = navItems
    .map((it) => ({ it, score: Math.log((map[it.to] || 0) + 1) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score);
  if (scored.length === 0) {
    return { items: navItems.slice(0, n), kind: "default" };
  }
  return { items: scored.slice(0, n).map((r) => r.it), kind: "history" };
}
